import React, { useEffect, useRef, useState } from "react";
import { Calculator as CalculatorIcon, X, Backspace } from "@phosphor-icons/react";
import ContentRenderer from "lib/components/ContentRenderer";
import ImportKatex from "lib/components/ImportKatex";

// Newton API (newton.now.sh redirects here) -- free, unauthenticated, CORS-open
// math microservice. Docs: https://newton.now.sh
const NEWTON_BASE = "https://newton.vercel.app/api/v2";

const OPERATIONS = [
  { value: "simplify", label: "Simplify" },
  { value: "factor", label: "Factor" },
  { value: "derive", label: "Derivative" },
  { value: "integrate", label: "Integral" },
  { value: "zeroes", label: "Zeroes" },
  { value: "tangent", label: "Tangent line" },
  { value: "area", label: "Area under curve" },
  { value: "cos", label: "cos" },
  { value: "sin", label: "sin" },
  { value: "tan", label: "tan" },
  { value: "arccos", label: "arccos" },
  { value: "arcsin", label: "arcsin" },
  { value: "arctan", label: "arctan" },
  { value: "abs", label: "Absolute value" },
  { value: "log", label: "Logarithm" },
] as const;

const HINTS: Record<string, string> = {
  tangent: "Format: c|f(x) -- e.g. 2|x^3",
  area: "Format: c:d|f(x) -- e.g. 2:4|x^3",
  log: "Format: base|value -- e.g. 2|8",
};

// Each key is what's shown on the button vs. what actually gets inserted
// into the expression (Newton/its underlying parser wants "*" not "x" for
// multiplication, "pi" not "π", etc.)
const KEYPAD: { label: string; insert: string }[][] = [
  [
    { label: "(", insert: "(" },
    { label: ")", insert: ")" },
    { label: "^", insert: "^" },
    { label: "|", insert: "|" },
  ],
  [
    { label: "7", insert: "7" },
    { label: "8", insert: "8" },
    { label: "9", insert: "9" },
    { label: "÷", insert: "/" },
  ],
  [
    { label: "4", insert: "4" },
    { label: "5", insert: "5" },
    { label: "6", insert: "6" },
    { label: "×", insert: "*" },
  ],
  [
    { label: "1", insert: "1" },
    { label: "2", insert: "2" },
    { label: "3", insert: "3" },
    { label: "−", insert: "-" },
  ],
  [
    { label: "0", insert: "0" },
    { label: ".", insert: "." },
    { label: "x", insert: "x" },
    { label: "+", insert: "+" },
  ],
  [
    { label: "π", insert: "pi" },
    { label: "√", insert: "sqrt(" },
    { label: ",", insert: "," },
    { label: ":", insert: ":" },
  ],
];

export default function GlobalCalculator({
  visible = true,
  onClose,
}: {
  visible?: boolean;
  onClose?: () => void;
}) {
  const [operation, setOperation] = useState<(typeof OPERATIONS)[number]["value"]>("simplify");
  const [expression, setExpression] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const displayRef = useRef<HTMLInputElement>(null);

  const dragRef = useRef<{ startX: number; startY: number; left: number; top: number } | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("global_calculator_pos_v1");
      if (raw) {
        setPos(JSON.parse(raw));
      } else if (typeof window !== "undefined" && window.innerWidth >= 640) {
        setPos({
          left: Math.max(12, window.innerWidth - 16 - 320),
          top: Math.max(80, window.innerHeight - 520 - 20),
        });
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (pos) localStorage.setItem("global_calculator_pos_v1", JSON.stringify(pos));
  }, [pos]);

  const hidden = !visible;

  const startDrag = (e: React.PointerEvent) => {
    if (typeof window !== "undefined" && window.innerWidth < 640) return;
    const targetInteractive = (e.target as HTMLElement).closest("button, select, input");
    if (targetInteractive) return;
    if (!pos) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, left: pos.left, top: pos.top };
    setDragging(true);

    const onMove = (ev: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      setPos({
        left: Math.max(8, Math.min(window.innerWidth - 320, dragRef.current.left + dx)),
        top: Math.max(8, Math.min(window.innerHeight - 100, dragRef.current.top + dy)),
      });
    };
    const onUp = () => {
      setDragging(false);
      dragRef.current = null;
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  // Inserts at the cursor position (or the end, if the display never had
  // focus) instead of always appending -- makes the keypad behave like a
  // real calculator's keys rather than a "type at the end only" text box.
  const insertToken = (token: string) => {
    const el = displayRef.current;
    const start = el?.selectionStart ?? expression.length;
    const end = el?.selectionEnd ?? expression.length;
    const next = expression.slice(0, start) + token + expression.slice(end);
    setExpression(next);
    requestAnimationFrame(() => {
      el?.focus();
      const caret = start + token.length;
      el?.setSelectionRange(caret, caret);
    });
  };

  const backspace = () => {
    const el = displayRef.current;
    const start = el?.selectionStart ?? expression.length;
    const end = el?.selectionEnd ?? expression.length;
    if (start === end && start > 0) {
      setExpression(expression.slice(0, start - 1) + expression.slice(end));
      requestAnimationFrame(() => el?.setSelectionRange(start - 1, start - 1));
    } else if (start !== end) {
      setExpression(expression.slice(0, start) + expression.slice(end));
      requestAnimationFrame(() => el?.setSelectionRange(start, start));
    }
  };

  const clearAll = () => {
    setExpression("");
    setResult(null);
    setError(null);
    displayRef.current?.focus();
  };

  const solve = async () => {
    const expr = expression.trim();
    if (!expr) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`${NEWTON_BASE}/${operation}/${encodeURIComponent(expr)}`);
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      if (data?.result === undefined || data?.result === null) {
        setError("Couldn't solve that expression -- check the syntax.");
      } else {
        setResult(String(data.result));
      }
    } catch {
      setError("Couldn't reach the calculator service. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const mobileAnchorProps = pos ? {} : { className: "fixed z-50 right-4 bottom-20" };

  return (
    <div
      {...mobileAnchorProps}
      aria-hidden={hidden}
      style={
        hidden
          ? { display: "none" }
          : pos
            ? { position: "fixed", left: pos.left + "px", top: pos.top + "px" }
            : undefined
      }
    >
      {!hidden && <ImportKatex />}
      <div className="w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg p-3">
        <div
          className={`flex items-center justify-between mb-2.5 ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
          onPointerDown={startDrag}
        >
          <div className="flex items-center gap-2">
            <CalculatorIcon className="text-primary" size={16} />
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">
              Math Calculator
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close calculator"
          >
            <X size={14} />
          </button>
        </div>

        <select
          value={operation}
          onChange={(e) => {
            setOperation(e.target.value as typeof operation);
            setResult(null);
            setError(null);
          }}
          className="w-full mb-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm px-2.5 py-1.5"
        >
          {OPERATIONS.map((op) => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </select>

        {/* Calculator "screen" */}
        <input
          ref={displayRef}
          type="text"
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && solve()}
          placeholder="e.g. x^2 + 2x"
          className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-right text-lg font-mono px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/30"
        />

        {HINTS[operation] && (
          <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">{HINTS[operation]}</p>
        )}

        {/* Keypad */}
        <div className="mt-2 grid grid-cols-4 gap-1.5">
          {KEYPAD.flat().map((key) => (
            <button
              key={key.label}
              type="button"
              onClick={() => insertToken(key.insert)}
              className="h-9 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 text-sm font-medium transition-colors"
            >
              {key.label}
            </button>
          ))}
          <button
            type="button"
            onClick={backspace}
            aria-label="Backspace"
            className="h-9 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 flex items-center justify-center transition-colors"
          >
            <Backspace size={16} />
          </button>
          <button
            type="button"
            onClick={clearAll}
            className="h-9 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 text-sm font-semibold transition-colors"
          >
            AC
          </button>
          <button
            type="button"
            onClick={solve}
            disabled={loading || !expression.trim()}
            className="col-span-2 h-9 rounded-lg bg-primary hover:bg-primary-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Solving..." : "Solve"}
          </button>
        </div>

        {error && (
          <p className="mt-2.5 text-xs text-red-600 dark:text-red-400">{error}</p>
        )}
        {result !== null && !error && (
          <div className="mt-2.5 rounded-lg bg-primary-50 dark:bg-primary-900/20 px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-primary dark:text-primary-300">
              Result
            </p>
            <div className="text-base text-gray-900 dark:text-gray-100 break-words [&_p]:m-0">
              <ContentRenderer>{`$${result}$`}</ContentRenderer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
